import { useMutation } from '@apollo/client';
import {
  CREATE_ORGANIZATION_PAY_PERIOD,
  UPDATE_ORGANIZATION_PAY_PERIOD,
} from 'apollo/mutations/organization-pay-period';
import { IPayrollData } from 'containers/onboarding/owner-onboarding/OwnerOnboardingPayrollSettings/OwnerOnboardingPayrollSettings';
import _, { isNil, toNumber } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { organizationSetAction } from 'store/organization/set/organizationSetAction';
import IOrganization from 'types/Organization';
import IOrganizationPayPeriod, {
  IOrganizationPayPeriodCreateInput,
  IOrganizationPayPeriodUpdateInput,
} from 'types/OrganizationPayPeriod';
import { DayOfWeekValue } from 'types/enum/DayOfWeek';
import { PayPeriodTypeValue } from 'types/enum/PayPeriodType';
import { convertDayToNumber, isoTimeStampUtc } from 'utils/dateUtils';
import { removeSuffiFromNumber } from 'utils/numberUtils';
import { getDayOfWeek, getPayPeriodType } from 'utils/payPeriodUtils';
import { uuid } from 'utils/uuidUtils';
import useOrganization from './store/useOrganization';
import { OrganizationPayPeriodsQuery, OrganizationPayPeriodsQueryVariables, SortOrder } from '__generated__/graphql';
import { ORGANIZATION_PAY_PERIODS_QUERY } from 'apollo/queries/organization-pay-periods';
import useGraphQLClient from './graphql/useGraphQLClient/useGraphQLClient';

export default function useOrganizationPayPeriod() {
  const org = useOrganization();
  const client = useGraphQLClient();
  const [createOrganizationPayPeriodMutation] = useMutation<{ createOrganizationPayPeriod: IOrganizationPayPeriod }>(
    CREATE_ORGANIZATION_PAY_PERIOD
  );
  const [updateOrganizationPayPeriodMutation] = useMutation<{ updateOrganizationPayPeriod: IOrganizationPayPeriod }>(
    UPDATE_ORGANIZATION_PAY_PERIOD
  );
  const dispatch = useDispatch();

  const getPayPeriods = async () => {
    const response = await client.request<OrganizationPayPeriodsQuery, OrganizationPayPeriodsQueryVariables>({
      document: ORGANIZATION_PAY_PERIODS_QUERY,
      variables: {
        filter: {
          deletedOn: { isNull: true },
        },
        sort: [
          {
            startDate: SortOrder.Desc,
          },
        ],
      },
    });

    if (isNil(response.organizationPayPeriods)) {
      throw Error(`result was ${response.organizationPayPeriods}`);
    }

    return response.organizationPayPeriods;
  };

  async function createPayPeriod(data: IPayrollData) {
    const item: IOrganizationPayPeriodCreateInput = {
      id: uuid(),
      organizationId: org.id!,
      payPeriodType: getPayPeriodType(data.payPeriodType),
      ...getPayPeriodData(data),
      createdOn: isoTimeStampUtc(),
    };

    const result = await createOrganizationPayPeriodMutation({ variables: { organizationPayPeriod: item } });

    if (result.data) {
      const newOrg = cloneDeep(org);
      newOrg.organizationPayPeriod = newOrg.organizationPayPeriod!.concat(result.data.createOrganizationPayPeriod);
      dispatch(organizationSetAction(newOrg as IOrganization));
    }

    return result.data?.createOrganizationPayPeriod;
  }

  function getPayPeriodData(data: IPayrollData, isBeginning?: boolean) {
    const currentDate = DateTime.utc().startOf('day');

    function setDayOfTheWeek(): DateTime {
      if (data.payPeriodType === PayPeriodTypeValue.WEEKLY) {
        const payPeriodStartDay: DayOfWeekValue = data.weeklyStartDay ?? DayOfWeekValue.MONDAY;
        const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const targetDayOfWeek = daysOfWeek.indexOf(payPeriodStartDay.toLowerCase()) + 1;
        const previousMostRecentWeekDate = currentDate.set({ weekday: targetDayOfWeek });

        return previousMostRecentWeekDate;
      } else if (data.payPeriodType === PayPeriodTypeValue.BIWEEKLY) {
        const biWeeklyPayPeriodDay = data.biWeeklyPayPeriodDay;
        const biWeeklySeconds = toNumber(biWeeklyPayPeriodDay);
        const biWeeklyDate = DateTime.fromMillis(biWeeklySeconds * 1000);
        return biWeeklyDate;
      } else if (data.payPeriodType === PayPeriodTypeValue.SEMIMONTHLY) {
        const firstSemiMonthlyDate = currentDate.set({ day: removeSuffiFromNumber(data.semiMonthlyDays[0]) });
        return firstSemiMonthlyDate;
      } else if (data.payPeriodType === PayPeriodTypeValue.MONTHLY) {
        const monthlyDate = currentDate.set({ day: removeSuffiFromNumber(data.monthlyDay) });
        return monthlyDate;
      } else {
        return currentDate;
      }
    }

    let startDate = new Date().toISOString();
    const beginningISO = '1970-01-05T00:00:00'; // The is the start date for all beginning pay periods and should be preserved

    switch (data.payPeriodType) {
      case PayPeriodTypeValue.WEEKLY:
        startDate = data.effectiveDate?.value
          ? DateTime.fromJSDate(data.effectiveDate.value).toUTC().startOf('day').toISO()
          : isBeginning
            ? beginningISO
            : setDayOfTheWeek().minus({ weeks: 8 }).toUTC().startOf('day').toISO();
        break;
      case PayPeriodTypeValue.BIWEEKLY:
        startDate = data.effectiveDate?.value
          ? DateTime.fromJSDate(data.effectiveDate.value).toUTC().startOf('day').toISO()
          : isBeginning
            ? beginningISO
            : setDayOfTheWeek().minus({ weeks: 8 }).toUTC().startOf('day').toISO();
        break;
      case PayPeriodTypeValue.MONTHLY:
        startDate = data.effectiveDate?.value
          ? DateTime.fromJSDate(data.effectiveDate.value).toUTC().startOf('day').toISO()
          : isBeginning
            ? beginningISO
            : setDayOfTheWeek().minus({ months: 2 }).toUTC().startOf('day').toISO();
        break;
      case PayPeriodTypeValue.SEMIMONTHLY:
        startDate = data.effectiveDate?.value
          ? DateTime.fromJSDate(data.effectiveDate.value).toUTC().startOf('day').toISO()
          : isBeginning
            ? beginningISO
            : setDayOfTheWeek().minus({ months: 2 }).toUTC().startOf('day').toISO();
        break;
      default:
        break;
    }

    if (data.payPeriodType === PayPeriodTypeValue.MONTHLY) {
      const day = convertDayToNumber(data.monthlyDay);

      return {
        startDate,
        dayOne: day,
        dayTwo: null,
      };
    } else if (data.payPeriodType === PayPeriodTypeValue.BIWEEKLY) {
      const effectivePayrollStartDate = data.effectiveDate?.value
        ? _.toNumber(data.effectiveDate.value.getTime() / 1000)
        : 0;
      const payPeriodStartDate = DateTime.fromSeconds(effectivePayrollStartDate!, { zone: 'system' });
      const day1 = Math.trunc((effectivePayrollStartDate - payPeriodStartDate.toSeconds()) / (3600 * 24)) % 14; // Formula from API team.
      return {
        startDate,
        dayOne: day1,
        dayTwo: null,
      };
    } else if (data.payPeriodType === PayPeriodTypeValue.SEMIMONTHLY) {
      const day1 = convertDayToNumber(data.semiMonthlyDays![0]);
      const day2 = convertDayToNumber(data.semiMonthlyDays![1]);

      return {
        startDate,
        dayOne: day1,
        dayTwo: day2,
      };
    } else {
      return {
        startDate,
        dayOne: getDayOfWeek(data.weeklyStartDay),
        dayTwo: null,
      };
    }
  }

  async function deletePayPeriod(payPeriod: IOrganizationPayPeriod) {
    const item: IOrganizationPayPeriodUpdateInput = {
      id: payPeriod.id,
      payPeriodType: payPeriod.payPeriodType,
      startDate: payPeriod.startDate,
      dayOne: payPeriod.dayOne,
      deletedOn: isoTimeStampUtc(),
    };

    const result = await updateOrganizationPayPeriodMutation({ variables: { organizationPayPeriod: item } });

    if (result.data) {
      const newOrg = cloneDeep(org);
      newOrg.organizationPayPeriod = newOrg.organizationPayPeriod!.filter((o) => o.id !== payPeriod.id);
      dispatch(organizationSetAction(newOrg as IOrganization));
    }

    return result.data?.updateOrganizationPayPeriod;
  }

  async function updatePayPeriod(id: string, data: IPayrollData, isBeginning?: boolean) {
    const input: IOrganizationPayPeriodUpdateInput = {
      id,
      payPeriodType: getPayPeriodType(data.payPeriodType),
      ...getPayPeriodData(data, isBeginning),
    };

    const result = await updateOrganizationPayPeriodMutation({ variables: { organizationPayPeriod: input } });

    if (result.data) {
      const newOrg = cloneDeep(org);
      newOrg.organizationPayPeriod = newOrg.organizationPayPeriod!.map((o) =>
        o.id === input.id && result.data ? result.data.updateOrganizationPayPeriod : o
      );
      dispatch(organizationSetAction(newOrg as IOrganization));
    }

    return result.data?.updateOrganizationPayPeriod;
  }

  return { getPayPeriods, createPayPeriod, deletePayPeriod, updatePayPeriod };
}
